import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { blogPostSchema, companySchema, SITE } from '../config/seo';

const BlogDetailPage = () => {
  const { postId } = useParams();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get content based on language, with fallback
  const getTitle = () => {
    if (language === 'en') return post.title_en || post.title_tc;
    if (language === 'cn') return post.title_cn || post.title_tc;
    return post.title_tc;
  };

  const getContent = () => {
    if (language === 'en') return post.content_en || post.content_tc;
    if (language === 'cn') return post.content_cn || post.content_tc;
    return post.content_tc;
  };

  const getDescription = () => getContent()
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 155);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-6 py-20 text-center">
          <p className="text-xl text-slate-600">
            {language === 'tc' ? '文章不存在' : 'Article not found'}
          </p>
          <Link to="/blog" className="text-primary hover:underline mt-4 inline-block">
            {language === 'tc' ? '返回Blog頁面' : 'Back to Blog'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title={`${getTitle()}｜樹記海鮮`}
        description={getDescription()}
        path={`/blog/${post.post_id}`}
        language={language}
        image={post.image_url}
        type="article"
        schemas={[companySchema(), blogPostSchema(post, language)]}
      />
      <Header />

      <article className="py-20" data-testid="blog-detail">
        <div className="container mx-auto px-6 max-w-4xl">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-primary hover:gap-2 transition-all mb-8"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="mr-1 w-4 h-4" />
            {language === 'tc' ? '返回' : 'Back'}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {post.image_url && (
              <img
                src={post.image_url}
                alt={post[`title_${language}`]}
                className="w-full h-96 object-cover rounded-3xl mb-8"
              />
            )}

            <div className="flex items-center text-slate-500 mb-4">
              <Calendar className="w-5 h-5 mr-2" />
              {new Date(post.created_at).toLocaleDateString(language === 'tc' ? 'zh-HK' : 'en-US')}
            </div>

            <h1 className="text-5xl font-bold mb-8" data-testid="blog-detail-title">
              {getTitle()}
            </h1>

            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: getContent() }}
              data-testid="blog-detail-content"
            />
          </motion.div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;